import { ProfilePoint, HDDParameters, HDDPathPoint } from './types';

/**
 * Calculate the HDD borehole path with proper survey geometry
 *
 * Inclination convention (from vertical):
 *   - 0° = straight down
 *   - 90° = horizontal
 *   - 180° = straight up
 *
 * Entry at 12° from horizontal = 78° inclination
 * Hold section = 90° inclination (horizontal)
 * Exit at 12° from horizontal = 102° inclination
 *
 * Profile types:
 *   - 'continuous': Smooth curve down to target depth and back up, no flat section
 *   - 'build-hold-build': Entry curve, flat hold section, exit curve
 */
export function calculateHDDPath(
  profile: ProfilePoint[],
  params: HDDParameters
): HDDPathPoint[] {
  if (params.profileType === 'continuous') {
    return calculateContinuousPath(profile, params);
  } else {
    return calculateBuildHoldBuildPath(profile, params);
  }
}

/**
 * Continuous curve profile - smooth arc to target depth and back
 * No flat hold section, curves meet at the deepest point
 */
function calculateContinuousPath(
  profile: ProfilePoint[],
  params: HDDParameters
): HDDPathPoint[] {
  const totalHorizontalDistance = profile[profile.length - 1].distance;
  const startElevation = profile[0].elevation;
  const endElevation = profile[profile.length - 1].elevation;

  // Entry and exit angles from horizontal
  const entryAngleDeg = params.entryAngle;
  const exitAngleDeg = params.exitAngle || params.entryAngle;
  const entryAngleRad = entryAngleDeg * Math.PI / 180;
  const exitAngleRad = exitAngleDeg * Math.PI / 180;

  // Inclinations (from vertical)
  const entryInclination = 90 - entryAngleDeg;  // 78°
  const exitInclination = 90 + exitAngleDeg;    // 102°
  const holdInclination = 90;

  // Target depth below entry point
  const targetDepth = params.targetDepth || params.holdDepth;
  const maxDepthElevation = startElevation - targetDepth;

  // Calculate entry curve geometry to reach target depth
  // For inclination changing from 78° to 90°, vertical drop = integral of cos(inc)
  // Using the formula: vertical = R * (1 - cos(angle)) for a circular arc approximation
  // But we're using linear inclination change, so we integrate numerically

  // Calculate entry curve: what MD is needed to drop targetDepth with linear inc change?
  // We need to find kickoffMD such that integrating gives us targetDepth vertical drop
  const entryKickoffMD = findMDForVerticalDrop(entryInclination, holdInclination, targetDepth);
  const entryGeometry = calculateCurveGeometry(entryInclination, holdInclination, entryKickoffMD);

  // Calculate exit curve: need to rise from maxDepthElevation to endElevation
  const exitVerticalRise = endElevation - maxDepthElevation;
  let exitPullbackMD: number;
  let exitGeometry: { horizontalDist: number; vertical: number };

  if (exitVerticalRise > 0 && params.exitToSurface) {
    // Need to climb up
    exitPullbackMD = findMDForVerticalDrop(holdInclination, exitInclination, -exitVerticalRise);
    exitGeometry = calculateCurveGeometry(holdInclination, exitInclination, exitPullbackMD);
  } else {
    exitPullbackMD = 0;
    exitGeometry = { horizontalDist: 0, vertical: 0 };
  }

  // Check if curves fit within the total distance
  const totalCurveHoriz = entryGeometry.horizontalDist + exitGeometry.horizontalDist;

  if (totalCurveHoriz > totalHorizontalDistance) {
    // Curves are too long - scale down the target depth
    const scale = totalHorizontalDistance * 0.95 / totalCurveHoriz;
    return calculateContinuousPath(profile, {
      ...params,
      targetDepth: targetDepth * scale
    });
  }

  // If there's a gap between curves, add a short hold section
  const gapDistance = totalHorizontalDistance - totalCurveHoriz;
  const hasHoldSection = gapDistance > 1;  // More than 1m gap

  // Build the path
  const path: HDDPathPoint[] = [];
  const numPointsPerSection = 50;

  // === ENTRY CURVE ===
  for (let i = 0; i <= numPointsPerSection; i++) {
    const t = i / numPointsPerSection;
    const segmentMD = entryKickoffMD * t;

    const currentInclination = entryInclination + (holdInclination - entryInclination) * t;
    const angleFromHorizontal = currentInclination - 90;

    const pos = integratePosition(entryInclination, currentInclination, segmentMD);
    const horizontalDist = pos.horizontal;
    const elevation = startElevation - pos.vertical;

    // Ensure we don't go below target depth
    const clampedElevation = Math.max(elevation, maxDepthElevation);

    const angleChange = Math.abs(holdInclination - entryInclination);
    const dogleg = entryKickoffMD > 0 ? (angleChange / entryKickoffMD) * 30 : 0;

    path.push({
      distance: horizontalDist,
      elevation: clampedElevation,
      angle: angleFromHorizontal,
      curvature: dogleg
    });
  }

  // === HOLD SECTION (if there's a gap) ===
  if (hasHoldSection) {
    const holdStartHoriz = entryGeometry.horizontalDist;
    const holdEndHoriz = totalHorizontalDistance - exitGeometry.horizontalDist;

    for (let i = 1; i <= numPointsPerSection; i++) {
      const t = i / numPointsPerSection;
      const horizontalDist = holdStartHoriz + (holdEndHoriz - holdStartHoriz) * t;

      const groundElev = interpolateGroundElevation(profile, horizontalDist);
      let elevation = maxDepthElevation;

      // Adjust for clearance if needed
      const maxElev = groundElev - params.minClearance;
      if (elevation > maxElev) {
        elevation = maxElev;
      }

      path.push({
        distance: horizontalDist,
        elevation: elevation,
        angle: 0,
        curvature: 0
      });
    }
  }

  // === EXIT CURVE ===
  if (params.exitToSurface && exitPullbackMD > 0) {
    const exitStartHoriz = totalHorizontalDistance - exitGeometry.horizontalDist;
    const holdEndElevation = path[path.length - 1].elevation;

    // Scale to ensure we end at ground level
    const geometryVerticalRise = -exitGeometry.vertical;
    const neededVerticalRise = endElevation - holdEndElevation;
    const verticalScale = geometryVerticalRise > 0.1 ? neededVerticalRise / geometryVerticalRise : 1;

    for (let i = 1; i <= numPointsPerSection; i++) {
      const t = i / numPointsPerSection;
      const segmentMD = exitPullbackMD * t;

      const currentInclination = holdInclination + (exitInclination - holdInclination) * t;
      const angleFromHorizontal = currentInclination - 90;

      const pos = integratePosition(holdInclination, currentInclination, segmentMD);
      const horizontalDist = exitStartHoriz + pos.horizontal;

      const verticalRise = -pos.vertical * verticalScale;
      const elevation = holdEndElevation + verticalRise;

      const angleChange = Math.abs(exitInclination - holdInclination);
      const dogleg = exitPullbackMD > 0 ? (angleChange / exitPullbackMD) * 30 : 0;

      path.push({
        distance: horizontalDist,
        elevation: elevation,
        angle: angleFromHorizontal,
        curvature: dogleg
      });
    }
  }

  return path;
}

/**
 * Build/Hold/Build profile - entry curve, flat hold section, exit curve
 * Uses fixed kickoff and pullback distances
 */
function calculateBuildHoldBuildPath(
  profile: ProfilePoint[],
  params: HDDParameters
): HDDPathPoint[] {
  const totalHorizontalDistance = profile[profile.length - 1].distance;
  const startElevation = profile[0].elevation;
  const endElevation = profile[profile.length - 1].elevation;

  const entryInclination = 90 - params.entryAngle;
  const exitInclination = 90 + (params.exitAngle || params.entryAngle);
  const holdInclination = 90;

  const kickoffMD = params.kickoffDistance;
  const pullbackMD = params.pullbackDistance;

  const entryGeometry = calculateCurveGeometry(entryInclination, holdInclination, kickoffMD);
  const holdElevation = startElevation - entryGeometry.vertical;
  const exitGeometry = calculateCurveGeometry(holdInclination, exitInclination, pullbackMD);

  const holdHorizontalDist = totalHorizontalDistance - entryGeometry.horizontalDist - exitGeometry.horizontalDist;

  if (holdHorizontalDist < 0) {
    const totalCurveHoriz = entryGeometry.horizontalDist + exitGeometry.horizontalDist;
    const scale = totalHorizontalDistance * 0.9 / totalCurveHoriz;
    return calculateBuildHoldBuildPath(profile, {
      ...params,
      kickoffDistance: kickoffMD * scale,
      pullbackDistance: pullbackMD * scale
    });
  }

  const path: HDDPathPoint[] = [];
  const numPointsPerSection = 50;

  // === ENTRY CURVE ===
  for (let i = 0; i <= numPointsPerSection; i++) {
    const t = i / numPointsPerSection;
    const segmentMD = kickoffMD * t;

    const currentInclination = entryInclination + (holdInclination - entryInclination) * t;
    const angleFromHorizontal = currentInclination - 90;

    const pos = integratePosition(entryInclination, currentInclination, segmentMD);
    const elevation = startElevation - pos.vertical;

    const angleChange = Math.abs(holdInclination - entryInclination);
    const dogleg = kickoffMD > 0 ? (angleChange / kickoffMD) * 30 : 0;

    path.push({
      distance: pos.horizontal,
      elevation: elevation,
      angle: angleFromHorizontal,
      curvature: dogleg
    });
  }

  // === HOLD SECTION ===
  const holdStartHoriz = entryGeometry.horizontalDist;
  const holdEndHoriz = totalHorizontalDistance - exitGeometry.horizontalDist;

  for (let i = 1; i <= numPointsPerSection; i++) {
    const t = i / numPointsPerSection;
    const horizontalDist = holdStartHoriz + (holdEndHoriz - holdStartHoriz) * t;

    const groundElev = interpolateGroundElevation(profile, horizontalDist);
    let elevation = holdElevation;

    const maxElev = groundElev - params.minClearance;
    if (elevation > maxElev) {
      elevation = maxElev;
    }

    path.push({
      distance: horizontalDist,
      elevation: elevation,
      angle: 0,
      curvature: 0
    });
  }

  // === EXIT CURVE ===
  if (params.exitToSurface) {
    const holdEndElevation = path[path.length - 1].elevation;
    const geometryVerticalRise = -exitGeometry.vertical;
    const neededVerticalRise = endElevation - holdEndElevation;
    const verticalScale = geometryVerticalRise > 0.1 ? neededVerticalRise / geometryVerticalRise : 1;

    for (let i = 1; i <= numPointsPerSection; i++) {
      const t = i / numPointsPerSection;
      const segmentMD = pullbackMD * t;

      const currentInclination = holdInclination + (exitInclination - holdInclination) * t;
      const angleFromHorizontal = currentInclination - 90;

      const pos = integratePosition(holdInclination, currentInclination, segmentMD);
      const horizontalDist = holdEndHoriz + pos.horizontal;

      const verticalRise = -pos.vertical * verticalScale;
      const elevation = holdEndElevation + verticalRise;

      const angleChange = Math.abs(exitInclination - holdInclination);
      const dogleg = pullbackMD > 0 ? (angleChange / pullbackMD) * 30 : 0;

      path.push({
        distance: horizontalDist,
        elevation: elevation,
        angle: angleFromHorizontal,
        curvature: dogleg
      });
    }
  }

  return path;
}

/**
 * Find the measured depth needed to achieve a target vertical drop/rise
 * Uses binary search since the relationship is non-linear
 */
function findMDForVerticalDrop(inc1Deg: number, inc2Deg: number, targetVertical: number): number {
  // Binary search for the MD that gives us the target vertical
  let minMD = 0;
  let maxMD = Math.abs(targetVertical) * 20;  // Upper bound estimate

  for (let iter = 0; iter < 50; iter++) {
    const midMD = (minMD + maxMD) / 2;
    const geometry = integratePosition(inc1Deg, inc2Deg, midMD);

    if (Math.abs(geometry.vertical - targetVertical) < 0.01) {
      return midMD;
    }

    if (geometry.vertical < targetVertical) {
      minMD = midMD;
    } else {
      maxMD = midMD;
    }
  }

  return (minMD + maxMD) / 2;
}

/**
 * Calculate horizontal and vertical distances for a curve
 */
function calculateCurveGeometry(inc1Deg: number, inc2Deg: number, measuredDepth: number): {
  horizontalDist: number;
  vertical: number;
} {
  const pos = integratePosition(inc1Deg, inc2Deg, measuredDepth);
  return { horizontalDist: pos.horizontal, vertical: pos.vertical };
}

/**
 * Integrate position along a curve where inclination changes linearly
 */
function integratePosition(inc1Deg: number, inc2Deg: number, measuredDepth: number): {
  horizontal: number;
  vertical: number;
} {
  const numSteps = 100;
  const dMD = measuredDepth / numSteps;

  let horizontal = 0;
  let vertical = 0;

  for (let i = 0; i < numSteps; i++) {
    const t = (i + 0.5) / numSteps;
    const inc = inc1Deg + (inc2Deg - inc1Deg) * t;
    const incRad = inc * Math.PI / 180;

    horizontal += dMD * Math.sin(incRad);
    vertical += dMD * Math.cos(incRad);
  }

  return { horizontal, vertical };
}

/**
 * Interpolate ground elevation at a given distance along the profile
 */
function interpolateGroundElevation(profile: ProfilePoint[], distance: number): number {
  if (distance <= 0) return profile[0].elevation;
  if (distance >= profile[profile.length - 1].distance) {
    return profile[profile.length - 1].elevation;
  }

  for (let i = 0; i < profile.length - 1; i++) {
    if (profile[i + 1].distance >= distance) {
      const t = (distance - profile[i].distance) / (profile[i + 1].distance - profile[i].distance);
      return profile[i].elevation + t * (profile[i + 1].elevation - profile[i].elevation);
    }
  }

  return profile[profile.length - 1].elevation;
}

/**
 * Get default HDD parameters
 */
export function getDefaultHDDParameters(): HDDParameters {
  return {
    entryAngle: 12,
    exitToSurface: true,
    exitAngle: 12,
    targetDepth: 15,
    minClearance: 3,
    maxDogleg: 5,
    holdDepth: 10,
    kickoffDistance: 100,
    pullbackDistance: 100,
    profileType: 'build-hold-build'
  };
}
